import React, {Component} from 'react';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PhotoIcon from '@material-ui/icons/Photo';
import ErrorIcon from '@material-ui/icons/Error';
import DoneIcon from '@material-ui/icons/Done';
import CloseIcon from '@material-ui/icons/Close';
import {withStyles, withTheme} from '@material-ui/core/styles';
import {withTranslation} from 'react-i18next';
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import ListItem from "@material-ui/core/ListItem/ListItem";
import ListItemText from "@material-ui/core/ListItemText/ListItemText";
import IconButton from "@material-ui/core/IconButton/IconButton";
import Button from "@material-ui/core/Button";
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import PhotoSettingsDialog from "./PhotoSettingsDialog";
import PrintSettingsDialog from "./PrintSettingsDialog";
import { red, green } from '@material-ui/core/colors';
import { PhotoModeContext } from '../contexts/PhotoModeContext';

const styles = () => ({
  text: {
    overflow: 'hidden',
    '& > span': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    marginLeft: '12px',
    // Let Material-UI handle the spacing automatically
    marginRight: 0,
  },
  chip: {
    marginRight: '4px'
  },
  container: {
    position: 'relative'
  },
  progress: {
    position: 'absolute',
    top: '-3px',
    left: '-3px'
  },

  listItemContent: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    alignItems: 'center',
    gap: 0,
    width: '100%',
    minWidth: 0, // Important for text truncation
  },
});

class Printjob extends Component {
  constructor(props) {
    super(props)

    this.state = {
      settingsOpened: false,
      copies: props.copies,
    };

    this.remove = this.remove.bind(this);
    this.toggleSettings = this.toggleSettings.bind(this);
    this.closeSettings = this.closeSettings.bind(this);
    this.settingsChanged = this.settingsChanged.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.copies !== this.props.copies) {
      this.setState({ copies: this.props.copies });
    }
  }

  settingsChanged(changes) {
    this.props.onSettingsChanged(changes);
  }

  remove() {
    fetch(this.props.orderUrl('/printjobs/' + this.props.id + ''), {
      method: 'DELETE',
    }).then(function(response) {
      if (response.status === 404) {
        return;
      }
      return response.json();
    }).then((json) => {
      if (json) {
        this.props.updateOrder(json);
      }
    });
  }



  toggleSettings() {
    this.setState({settingsOpened: !this.state.settingsOpened});
  }

  closeSettings() {
    this.setState({settingsOpened: false});
  }

  render() {
    const disabled = this.props.failed || this.props.disabled;
    const { classes, colorEnabled, t, theme } = this.props;

    // Determine avatar background color
    let avatarBackgroundColor = 'default';
    if (this.props.errorMessage !== null) {
      avatarBackgroundColor = red[500];
    } else if (this.props.printed) {
      avatarBackgroundColor = green[500];
    } else if (this.props.pageCount !== null) {
      avatarBackgroundColor = theme.palette.secondary.main;
    }

    // Determine avatar icon
    let avatarIcon;
    if (this.props.errorMessage !== null) {
      avatarIcon = <ErrorIcon />;
    } else if (this.props.printed) {
      avatarIcon = <DoneIcon />;
    } else {
      avatarIcon = this.context.isPhotoMode ? <PhotoIcon /> : <InsertDriveFileIcon />;
    }

    return(
      <ListItem
        data-cy="printjob"
      >
        <div className={classes.listItemContent}>
          <div className={classes.container}>
            <Badge
              overlap="circle"
              badgeContent={this.props.pageCount === null ? '?' : this.props.pageCount}
              color="primary"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
            >
              <Avatar
                style={{
                  backgroundColor: avatarBackgroundColor,
                }}
              >
                {avatarIcon}
              </Avatar>
            </Badge>
            { (this.props.inProgress || (this.props.errorMessage == null && this.props.pageCount === null)) && <CircularProgress className={classes.progress} size={46}/> }
          </div>

          <ListItemText
            className={classes.text}
            data-cy="printjob.text"
            primary={this.props.name}
            secondary={
              <>
                {this.props.errorMessage &&
                  <span data-cy="printjob.error">
                    {this.props.errorMessage}
                  </span>
                }
                {!this.props.errorMessage &&
                  <>
                    <span>
                      {!this.context.isPhotoMode && (this.props.isDuplex ? t('duplex') : t('simplex'))}
                      {!this.context.isPhotoMode && ' / '}
                      {t('copies')}: {this.props.copies}
                    </span>
                  </>
                }
              </>
            }
          />

          <div>
            <IconButton
              onClick={this.remove}
              disabled={disabled && !this.props.document.is_failed}
              color={this.props.document.is_failed ? "primary" : "default"}
              data-cy="printjob.removeButton"
            >
              <CloseIcon />
            </IconButton>

            <Button
              onClick={this.toggleSettings}
              disabled={disabled}
              variant="contained"
              color="primary"
              data-cy="printjob.settingsButton"
            >
              {t('settings')}
            </Button>
          </div>
        </div>

        {this.context.isPhotoMode ? (
          <PhotoSettingsDialog
            open={this.state.settingsOpened}
            onClose={this.closeSettings}
            onSettingsChanged={this.settingsChanged}
            orderUrl={this.props.orderUrl}
            id={this.props.id}
            pageCount={this.props.pageCount}
            copies={this.state.copies}
          />
        ) : (
          <PrintSettingsDialog
            open={this.state.settingsOpened}
            onClose={this.closeSettings}
            onSettingsChanged={this.settingsChanged}
            orderUrl={this.props.orderUrl}
            id={this.props.id}
            pageCount={this.props.pageCount}
            isGrayscale={this.props.isGrayscale}
            isDuplex={this.props.isDuplex}
            disabled={disabled}
            colorEnabled={colorEnabled}
            copies={this.state.copies}
          />
        )}
      </ListItem>
    )
  }
}

Printjob.contextType = PhotoModeContext;

export default withStyles(styles)(withTheme(withTranslation()(Printjob)));
